<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import {
	DialogContent,
	DialogOverlay,
	DialogPortal,
	DialogRoot,
	DialogTitle,
	VisuallyHidden,
} from 'reka-ui';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import Icon from '@/components/ui/Icon.vue';
import { usePageSearch } from '@/composables/usePageSearch';

const router = useRouter();
const { isOpen, groups, isSignedIn } = usePageSearch();

const handleSelect = (path: string) => {
	isOpen.value = false;

	// Pushing the route already open is a duplicated navigation; skip it rather
	// than let the resulting failure surface.
	if (path !== router.currentRoute.value.path) {
		router.push(path);
	}
};

// Cmd+K on macOS, Ctrl+K elsewhere. preventDefault matters: Chrome binds Ctrl+K
// to address bar search, so without it the browser acts too.
const handleShortcut = (event: KeyboardEvent) => {
	if (event.key?.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) {
		return;
	}

	if (!isSignedIn.value) {
		return;
	}

	event.preventDefault();
	isOpen.value = !isOpen.value;
};

onMounted(() => {
	window.addEventListener('keydown', handleShortcut);
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleShortcut);
});
</script>

<template>
	<!-- Composed from the reka-ui primitives rather than the vendored
	     CommandDialog, whose panel and overlay classes are fixed and cannot be
	     softened from the outside. Closing unmounts this content, which discards
	     the Command's internal filter state — so reopening always starts from the
	     full list without anything here having to reset the query. -->
	<DialogRoot v-model:open="isOpen">
		<DialogPortal>
			<DialogOverlay
				class="cp-overlay fixed inset-0 z-50 bg-[color:var(--bg-inverse)]/40 backdrop-blur-[3px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none"
			/>
			<DialogContent
				class="cp-panel fixed left-1/2 top-[12vh] z-50 w-[min(620px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden border border-[color:var(--depot-border)] bg-[color:var(--bg-elev)] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none"
			>
				<VisuallyHidden>
					<DialogTitle>Search pages</DialogTitle>
				</VisuallyHidden>

				<Command class="bg-transparent">
					<CommandInput
						placeholder="Search pages…"
						class="cp-input h-[52px] text-[14px]"
					/>

					<CommandList class="max-h-[min(58vh,420px)] px-2 py-2">
						<CommandEmpty class="cp-empty">No pages match that search.</CommandEmpty>

						<CommandGroup
							v-for="group in groups"
							:key="group.section"
							:aria-label="group.section"
							class="cp-group px-0"
						>
							<!-- Rendered rather than passed as `heading` so it can carry the
							     sidebar's own eyebrow treatment; the group still hides itself
							     when nothing in it matches. -->
							<div class="cp-eyebrow">{{ group.section }}</div>

							<CommandItem
								v-for="entry in group.entries"
								:key="entry.path"
								:value="entry.path"
								class="cp-row"
								@select="handleSelect(entry.path)"
							>
								<span class="cp-tile">
									<Icon :name="entry.icon" :size="16" :stroke-width="1.7" />
								</span>
								<span class="cp-text">
									<span class="cp-label">{{ entry.label }}</span>
									<span class="cp-path">{{ entry.path }}</span>
								</span>
								<!-- Matched by the filter but not shown: CommandItem registers an
								     item's rendered textContent as its filter value, and
								     textContent ignores CSS. This is what makes the section name
								     and the menu description searchable without cluttering the
								     row. -->
								<span class="hidden">{{ entry.keywords }}</span>
							</CommandItem>
						</CommandGroup>
					</CommandList>

					<div class="cp-foot">
						<span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
						<span><kbd>↵</kbd> open</span>
						<span><kbd>esc</kbd> close</span>
					</div>
				</Command>
			</DialogContent>
		</DialogPortal>
	</DialogRoot>
</template>

<style scoped>
/* Radius, shadow and the row treatment live here rather than inline: several
   need to reach into the vendored command components, whose own utility classes
   would otherwise win on specificity. */
.cp-panel {
	border-radius: var(--r-xl);
	box-shadow: var(--shadow-lg);
}

/* CommandInput puts its `class` on the inner <input>; the divider and padding
   live on its wrapper, which is only reachable by its own attribute hook. */
.cp-panel :deep([cmdk-input-wrapper]) {
	border-color: var(--depot-border);
	padding: 0 14px;
}
.cp-panel :deep([cmdk-input-wrapper] svg) {
	width: 15px;
	height: 15px;
	margin-right: 10px;
	color: var(--fg-faint);
	opacity: 1;
}
.cp-panel :deep(.cp-input::placeholder) {
	color: var(--fg-faint);
}

/* Matches the sidebar's `.group-label`, so the palette reads as that rail's own
   search rather than a generic dialog dropped on top of the app. */
.cp-eyebrow {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--fg-faint);
	padding: 10px 10px 6px;
}
.cp-group:first-child .cp-eyebrow {
	padding-top: 4px;
}

.cp-panel :deep(.cp-row) {
	gap: 12px;
	padding: 7px 10px;
	border-radius: var(--r-md);
	border: 1px solid transparent;
}
.cp-panel :deep(.cp-row[data-highlighted]) {
	background: var(--bg-sunk);
	border-color: var(--depot-border);
}

.cp-tile {
	display: flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	border-radius: var(--r-sm);
	background: var(--accent-soft);
	color: var(--depot-accent);
}

.cp-text {
	display: flex;
	min-width: 0;
	flex-direction: column;
	gap: 1px;
}
.cp-label {
	font-size: 13px;
	color: var(--fg);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
/* The path is the honest second line: it says where Enter actually goes, and the
   mono face keeps it from competing with the title. */
.cp-path {
	font-family: var(--font-mono);
	font-size: 10.5px;
	color: var(--fg-faint);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.cp-panel :deep(.cp-empty) {
	padding: 28px 10px;
	font-size: 13px;
	color: var(--fg-muted);
}

.cp-foot {
	display: flex;
	gap: 14px;
	align-items: center;
	border-top: 1px solid var(--depot-border);
	background: var(--bg-sunk);
	padding: 8px 12px;
	font-size: 10.5px;
	color: var(--fg-faint);
}
.cp-foot kbd {
	font-family: var(--font-mono);
	font-size: 10px;
	border: 1px solid var(--depot-border);
	background: var(--bg-elev);
	border-radius: var(--r-xs);
	padding: 1px 4px;
	margin-right: 3px;
	color: var(--fg-muted);
}

@media (max-width: 640px) {
	.cp-foot {
		display: none;
	}
}
</style>
